<template>
  <div class="recipients-upload">
    <h2>{{ $t('W2P_RECIPIENTS') }}</h2>
    <p class="small paragraph">{{ $t('W2P_RECIPIENTS_DESCRIPTION_UPLOAD') }}</p>
    <how-to-upload-recipients
      @downloadFile="downloadSampleFile"
    ></how-to-upload-recipients>
    <upload-component
      accepted="xls|xlsx"
      :uploadPercentage="uploadPercentage"
      :isLoading="isLoading"
      :isInvalid="!isValid"
      :isFinished="isFinished"
      :activeFileName="activeFile.name"
      @filesAdded="handleFiles($event.files)"
      @reset="reset"
    >
      <img
        slot="image"
        src="@/assets/images/file-upload-address.svg"
        :alt="$t('W2P_ALT_BLUE_FOLDER_ALLOWED_FORMATS')"
      />
      <p slot="headline" class="headline" v-if="!isSmallScreen">
        {{ $t('W2P_RECIPIENTS_DRAG_FILE') }}
        <button class="browse-button">{{ $t('W2P_RECIPIENTS_BROWSE') }}</button>
      </p>
      <button slot="headline" class="browse-button" v-else>
        {{ $t('W2P_RECIPIENTS_BROWSE_FOR_A_FILE') }}
      </button>
      <p slot="description" class="description">
        {{ $t('W2P_RECIPIENTS_UPLOAD_DESCRIPTION') }}
      </p>
    </upload-component>

    <div class="message-container error" v-if="errorMessages.length">
      <div>
        <pn-icon
          symbol="alert-exclamation-circle"
          color="warning"
          size="medium"
          solid
        ></pn-icon>
      </div>
      <div>
        <p
          class="small"
          v-html="
            errCounts
              ? $t('W2P_RECIPIENTS_FILE_VALIDATION_ERROR', {
                  number: errCounts,
                  filename: activeFile.name
                })
              : $t('W2P_RECIPIENTS_FILE_UPLOAD_ERROR')
          "
        ></p>
      </div>
    </div>
    <div class="error-messages" v-if="errorMessages.length">
      <div class="error-row" v-for="(error, i) in errorMessages" :key="i">
        <p class="error-message bold" v-if="typeof error.key === 'string'">
          {{ $t(error.key) }}
        </p>
        <p class="error-message bold" v-else>
          {{ $t('W2P_RECIPIENTS_ROWS', { number: error.key }) }}
        </p>
        <p
          class="error-message"
          v-if="typeof error.message === 'string'"
          v-html="$t(error.message)"
        ></p>
        <p
          class="error-message"
          v-else
          v-html="$t(error.message.key, error.message.params)"
        ></p>
      </div>
    </div>
    <div v-if="showCorrectionFileDownload" class="error-buttons">
      <pn-button-container width="50%" size="medium">
        <button @click="downloadFile('errors', 'xls')">
          {{
            $t('W2P_RECIPIENTS_DOWNLOAD_CORRECTION_FILE', { extension: 'xls' })
          }}
        </button>
      </pn-button-container>
    </div>

    <div class="message-container success" v-if="isFinished">
      <div>
        <p class="small">
          {{ $t('W2P_RECIPIENTS_FILE_SUCCESS') }}
        </p>
        <p class="small" v-html="successMessage"></p>
      </div>
    </div>

    <div class="button-container">
      <pn-button-container secondary size="medium">
        <button @click="$router.push({ name: 'recipients' })">
          {{ $t('W2P_LETTER_BACK') }}
        </button>
      </pn-button-container>
      <pn-button-container size="medium">
        <button @click="next" :disabled="!isFinished" class="next">
          {{ $t('W2P_LETTER_NEXT') }}
        </button>
      </pn-button-container>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import xlsx from 'xlsx'
import { mapState, mapMutations } from 'vuex'
import * as utils from '@/helpers/utilHelper.js'

import WebSocketComponent from '@/components/WebSocketComponent'
import UploadComponent from '@/components/common/UploadComponent'
import HowToUploadRecipients from '@/components/how-to/upload-recipients/HowToUploadRecipientsComponent'

export default {
  mixins: [WebSocketComponent],
  beforeMount() {
    const addressState = this.$store.state.letter.address

    if (
      addressState.id &&
      addressState.selectedAddressOption === 'recipients-upload'
    ) {
      this.activeFile = {
        name: addressState.fileName
      }

      this.uploadPercentage = 100
    }

    this.addressValidation = addressState.addressValidation
  },
  data: () => ({
    table: [],
    uploadPercentage: 0,
    isLoading: false,
    activeFile: {},
    addressValidation: {}
  }),
  components: {
    UploadComponent,
    HowToUploadRecipients
  },
  computed: {
    ...mapState(['market', 'lang']),
    ...mapState('letter/upload', ['letterOptions']),
    ...mapState('letter/address', ['id']),
    ...mapState('letter/recipient', ['list']),
    service() {
      return this.letterOptions.includePaymentSlip
        ? 'paymentSlips'
        : 'information'
    },
    isSmallScreen() {
      return window.innerWidth < 800
    },
    isFinished() {
      return this.isValid && this.uploadPercentage === 100
    },
    isValid() {
      const { valid, invalidCount } = this.addressValidation

      return valid && invalidCount === 0
    },
    countsLimit() {
      return this.$store.state.cart.productDelivery.addressLimit
    },
    errCounts() {
      return this.addressValidation.invalidCount || this.errorMessages.length
    },
    errorMessages() {
      let errors = []

      const {
        missingHeaders,
        tooManyRows,
        errorRowInformation,
        clientError
      } = this.addressValidation

      if (missingHeaders) {
        errors.push({
          key: 'INVALID_FORMAT',
          message: 'RECIPIENTS_PAGE_INVALID_FORMAT'
        })
      }

      if (tooManyRows) {
        errors.push({
          key: 'INVALID_FORMAT',
          message: 'RECIPIENTS_PAGE_ERROR_TOO_MANY_ROWS'
        })
      }

      if (errorRowInformation && errorRowInformation.length) {
        errorRowInformation.forEach(err => {
          errors.push({
            key: err.count,
            message: err.errorMessage
          })
        })
      }

      if (clientError && clientError.length) {
        errors = errors.concat(clientError)
      }

      return errors
    },
    showCorrectionFileDownload() {
      const { valid, invalidCount } = this.addressValidation

      return valid && !!invalidCount && invalidCount > 0
    },
    addressCount() {
      const data = this.addressValidation

      if (!data.total) {
        return
      }

      if (data.zoneCount[0]) {
        return {
          domestic: data.zoneCount[0],
          international: data.total - data.zoneCount[0]
        }
      }

      return {
        domestic: 0,
        international: data.total
      }
    },
    successMessage() {
      if (!this.addressCount) {
        return
      }
      const { domestic, international } = this.addressCount

      if (domestic > 0 && international > 0) {
        return this.$t('W2P_RECIPIENTS_DOMESTIC_AND_INTERNATIONAL', {
          domestic,
          international
        })
      } else {
        return this.$t('W2P_RECIPIENTS_ADDRESSES_COUNTS', {
          count: domestic > 0 ? domestic : international
        })
      }
    }
  },
  methods: {
    ...mapMutations('letter/upload', ['SET_MATERIAL']),
    ...mapMutations({
      saveAddresses(commit, payload) {
        return commit('letter/address/SAVE', payload)
      },
      resetUploadState(commit) {
        return commit('letter/address/RESET_STATE')
      },
      resetRecipients(commit) {
        return commit('letter/recipient/RESET_STATE_ALL')
      }
    }),
    next() {
      this.$router.push({ name: 'delivery' })
    },
    ...mapMutations('letter/address', ['SAVE_OPTION']),
    handleFiles(files) {
      this.reset()
      this.isLoading = true
      const item = files[0]

      this.activeFile = item.file

      if (!item.validated) {
        this.addressValidation.errorRowInformation = []

        this.addClientError({
          key: 'W2P_RECIPIENTS_FILE_TYPE',
          message: 'W2P_RECIPIENTS_FILE_FORMAT_ERROR'
        })

        this.isLoading = false

        return
      }

      const reader = new FileReader()

      reader.onload = function(e) {
        const data = e.target.result
        const uArr = new Uint8Array(data)
        const arr = utils.decodeArrBuffer(uArr)
        const wb = xlsx.read(btoa(arr), { type: 'base64' })
        this.checkRecipients(utils.sheetsToJson(wb))
      }.bind(this)

      reader.readAsArrayBuffer(this.activeFile)
    },
    checkRecipients(sheetData) {
      const key = Object.keys(sheetData)[0]

      if (sheetData[key] && this.validCounts(sheetData[key].length)) {
        this.getSignedUrl().then(response => {
          const requestId = response.folder.split('/')[1]

          this.socketOpen(
            process.env.VUE_APP_ADDRESS_VALIDATION_SOCKET_URL,
            requestId,
            this.checkAddressValidity,
            this.errCheckAddressValidity
          )

          this.uploadPercentage = 50

          this.uploadFile(response.url).catch(() => {
            this.errCheckAddressValidity()
          })
        })
      } else {
        this.addClientError({
          key: 'W2P_RECIPIENTS_COUNTS',
          message: {
            key: 'W2P_RECIPIENTS_COUNTS_ERROR',
            params: this.countsLimit
          }
        })

        this.isLoading = false
      }
    },
    checkAddressValidity(message) {
      const data = JSON.parse(message.data)

      this.addressValidation = data

      if (this.isValid) {
        // reset previewUrl to generate a new one
        this.SET_MATERIAL({
          key: 'previewUrl',
          value: ''
        })

        this.next()
      }

      // clear manually added recipients
      if (this.list.length) {
        this.resetRecipients()
      }

      this.saveAddresses({
        id: data.id,
        zoneCount: data.zoneCount,
        total: data.total,
        fileName: this.activeFile.name,
        addressValidation: this.addressValidation
      })

      this.uploadPercentage = 100
      this.isLoading = false
    },
    errCheckAddressValidity() {
      this.isLoading = false
    },
    addClientError(error) {
      this.addressValidation = {
        clientError: [error]
      }
    },
    validCounts(counts) {
      return counts >= this.countsLimit.min && counts <= this.countsLimit.max
    },
    reset() {
      this.resetUploadState()
      this.isLoading = false
      this.uploadPercentage = 0
      this.addressValidation = {}
    },
    getSignedUrl() {
      return axios({
        method: 'post',
        data: {
          name: 'uploadedAddress.' + this.activeFile.name.split('.').pop(),
          folder: 'csv',
          type: this.activeFile.type,
          meta: {
            service: this.service,
            originalFileName: this.activeFile.name
          }
        },
        url: `${process.env.VUE_APP_W2P_API_BASE_URL}/recipients/generatePresigned`
      }).then(response => {
        return response.data
      })
    },
    uploadFile(url) {
      return axios.put(url, this.activeFile, {
        headers: {
          'Content-Type': this.activeFile.type,
          'Cache-Control': 'no-cache'
        }
        // onUploadProgress: function(progressEvent) {
        //   this.uploadPercentage = parseInt(
        //     Math.round((progressEvent.loaded * 100) / progressEvent.total)
        //   )
        // }.bind(this)
      })
    },
    downloadFile(type, ext) {
      const baseUrl = process.env.VUE_APP_RECEPIENTS_BASE_URL
      const types = {
        errors: {
          url: `${baseUrl}/downloadInvalidDetails`,
          params: {
            id: this.id,
            index: 1,
            language: this.lang,
            service: this.service,
            fileType: ext
          }
        },
        samples: {
          url: `${baseUrl}/getSampleFile`,
          params: {
            language: this.lang,
            market: this.market,
            service: this.service,
            fileType: ext
          }
        }
      }
      axios({
        method: 'get',
        url: types[type].url,
        params: types[type].params
      }).then(response => {
        this.forceFileDownload(response)
      })
    },
    downloadSampleFile(data) {
      this.downloadFile(data.type, data.ext)
    },
    forceFileDownload(response) {
      const data =
        response.data.body !== undefined
          ? response.data.body
          : response.data.file
      const link = document.createElement('a')
      link.href = `data:${response.data.contentType};base64,${data}`
      link.setAttribute('download', response.data.name)
      document.body.appendChild(link)
      link.click()
    }
  },
  mounted() {
    this.SAVE_OPTION('recipients-upload')
    axios({
      method: 'get',
      url: `${process.env.VUE_APP_W2P_API_BASE_URL}/recipients/v2/getSampleColumns?service=${this.service}&market=${this.market}`
    }).then(response => {
      this.table = response.data.result
    })
  }
}
</script>

<style lang="scss" scoped>
.recipients-upload {
  .button-container {
    margin-top: 3.5em;
    width: 100%;
    display: flex;
    justify-content: space-between;

    pn-button-container {
      width: 70%;
      max-width: 15em;
      min-width: 0;
    }
  }
  .browse-button {
    padding: 0;
    border: none;
    background: none;
    font-size: 1em;
    color: $blue900;
  }

  p {
    color: $gray700;
    font-weight: 300;

    &.headline {
      margin-top: 1em;
    }
    &.description {
      font-size: small;
    }
  }

  .pn-dropzone-container {
    margin-bottom: 1.5em;
  }

  a {
    text-decoration: none;
    color: $blue700;
  }

  .paragraph {
    margin-top: 0.5em;
  }

  .message-container {
    display: flex;
    margin-top: 0.5em;

    &.success {
      p {
        color: $green900;
      }
    }
    &.error {
      border: 1px solid $warning;
      padding: 1.5em;
      margin-top: 0.5em;
      div:first-child {
        margin-right: 0.5em;
      }

      p {
        color: $black;
      }
    }
  }
  .error-messages {
    border: 1px solid $gray50;
    .error-row {
      display: flex;
      padding: 1em 1em;

      &:nth-child(odd) {
        background-color: $coral50;
      }

      &:nth-child(even) {
        background-color: $white;
      }

      .error-message {
        color: $black;

        &:nth-child(odd) {
          margin-right: 4em;
          white-space: nowrap;
        }

        &:nth-child(even) {
          margin-top: 0.12em;
        }
      }
    }
  }

  .error-buttons {
    display: flex;
    justify-content: space-between;

    pn-button-container {
      margin-top: 2em;
      width: 100%;

      &:first-of-type {
        margin-right: 1em;
      }
    }
  }
}
</style>
